// Handle the blocked users sync in the library moderation tools

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import type { UserAuthFields } from '@airhead-games/shared/types';
import { ApiFetchError } from '../../../transport/api/baseFetch';
import {
  initiateTwitchModScope,
  syncBlockedUsers,
} from '../../../transport/api/protectedApi';
import type { LibraryDialog } from '../libraryRouteTypes';

export type LibraryBlockedUsersSyncStatus =
  'idle' | 'syncing' | 'synced' | 'error';

export type LibraryBlockedUsersSyncResult = Exclude<
  LibraryBlockedUsersSyncStatus,
  'syncing'
>;

type UseLibraryBlockedUsersSyncParams = {
  user: UserAuthFields | null;
  hasModScopeGrant: boolean;
  dialog: LibraryDialog | null;
  setUser: (user: UserAuthFields | null) => void;
  openDialog: (dialog: LibraryDialog) => void;
};

const MOD_SCOPE_REDIRECT_PATH =
  '/library?section=player-access&dialog=blocked-users';

const isScopeRequiredBody = (body: unknown) =>
  typeof body === 'object' &&
  body !== null &&
  'error' in body &&
  (body as { error?: unknown }).error === 'scope_required';

export function useLibraryBlockedUsersSync({
  user,
  hasModScopeGrant,
  dialog,
  setUser,
  openDialog,
}: UseLibraryBlockedUsersSyncParams) {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledModScopeGrantRef = useRef(false);
  const inFlightSyncRef = useRef<Promise<LibraryBlockedUsersSyncResult> | null>(
    null
  );
  const [syncStatus, setSyncStatus] =
    useState<LibraryBlockedUsersSyncStatus>('idle');

  const requestTwitchModScope = useCallback(() => {
    initiateTwitchModScope({
      redirectTo: MOD_SCOPE_REDIRECT_PATH,
    });
  }, []);

  const resetBlockedUsersSyncStatus = useCallback(() => {
    setSyncStatus('idle');
  }, []);

  const handleSyncBlockedUsers =
    useCallback((): Promise<LibraryBlockedUsersSyncResult> => {
      if (!user) {
        openDialog('login');
        return Promise.resolve('idle');
      }

      // Dedupe concurrent callers (e.g. mod-scope-grant return + auto-sync
      // both firing in the same render cycle): the first caller owns the
      // request, others await the same promise.
      if (inFlightSyncRef.current) {
        return inFlightSyncRef.current;
      }

      const run = async (): Promise<LibraryBlockedUsersSyncResult> => {
        setSyncStatus('syncing');

        try {
          const response = await syncBlockedUsers();

          if (response.data?.success && response.data.user) {
            // Backend has already persisted blockedTwitchUsers + the two
            // gating flags. Hand the updated user to AuthContext; the
            // useUserSave user-effect propagates it into local modSettings.
            setUser(response.data.user);
            setSyncStatus('synced');
            return 'synced';
          }

          if (response.data?.error === 'scope_required') {
            // Full-page redirect to Twitch; component will unmount before any
            // status update would be observed, so we leave syncStatus alone.
            requestTwitchModScope();
            return 'idle';
          }

          throw new Error('Blocked user sync failed.');
        } catch (error) {
          if (
            error instanceof ApiFetchError &&
            isScopeRequiredBody(error.response)
          ) {
            requestTwitchModScope();
            return 'idle';
          }

          setSyncStatus('error');
          toast('Twitch sync failed. Try again.');
          return 'error';
        }
      };

      const promise = run().finally(() => {
        inFlightSyncRef.current = null;
      });
      inFlightSyncRef.current = promise;
      return promise;
    }, [openDialog, requestTwitchModScope, setUser, user]);

  useEffect(() => {
    if (
      !user?.authTwitch?.userId ||
      !hasModScopeGrant ||
      dialog !== 'blocked-users' ||
      handledModScopeGrantRef.current
    ) {
      return;
    }

    handledModScopeGrantRef.current = true;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('modScopeGranted');
    setSearchParams(nextParams, { replace: true });
    void handleSyncBlockedUsers();
  }, [
    dialog,
    handleSyncBlockedUsers,
    hasModScopeGrant,
    searchParams,
    setSearchParams,
    user,
  ]);

  return {
    syncStatus,
    handleSyncBlockedUsers,
    requestTwitchModScope,
    resetBlockedUsersSyncStatus,
  };
}
